/**
 * "Check printing" (Phase B §6): a trade line whose card has another printing worth noticeably more
 * or less than the one it went in as. An auto-locked scan goes in as the card's first printing, and
 * a photo can't tell Normal from Reverse Holo, so this is where a wrong printing costs money.
 *
 * A flag only: it never blocks completing the trade (owner decision, 2026-10-28). Prices come from
 * POST /scanner/lookup (USD cents), the same numbers the scanner shows; the line's own condition is
 * compared with the other printings' price for that condition.
 */
import { lookupPrintings, quoteFor, type LookupEntry } from './scannerLookup'
import { samePrinting } from './printings'

/** Another printing more than this far from the line's own (as a share of it) flags the line. */
export const PRINTING_PRICE_GAP = 0.1

export type PrintingPrice = { printing: string; label: string; usdCents: number }

export type PrintingCheck = {
  flagged: boolean
  /** The line's printing as the lookup names it, and its market price (null = unpriced). */
  chosen: { printing: string; label: string; usdCents: number | null }
  /** The card's other printings that have a price in this condition. */
  others: PrintingPrice[]
}

/**
 * The check for one line, or null while its lookup hasn't arrived (or the card isn't in the
 * catalogue). An unpriced line is not flagged: it went in at nothing and is priced by hand anyway.
 */
export function checkPrinting(
  entry: LookupEntry | null | undefined,
  printing: string | null | undefined,
  condition: string,
  gap = PRINTING_PRICE_GAP,
): PrintingCheck | null {
  if (!entry) return null
  const options = lookupPrintings(entry.card)
  const own = quoteFor(entry, printing ?? null, condition)
  const label = (value: string) => options.find((o) => o.value === value)?.label ?? value
  const others: PrintingPrice[] = []
  for (const option of options) {
    if (samePrinting(option.value, own.printing)) continue
    const usdCents = quoteFor(entry, option.value, condition).marketUsdCents
    if (usdCents && usdCents > 0) others.push({ printing: option.value, label: option.label, usdCents })
  }
  const base = own.marketUsdCents && own.marketUsdCents > 0 ? own.marketUsdCents : null
  const flagged = base !== null && others.some((o) => Math.abs(o.usdCents - base) / base > gap)
  return { flagged, chosen: { printing: own.printing, label: label(own.printing), usdCents: base }, others }
}

/** The badge's tooltip: "In as Normal (US$1.20). Holofoil US$8.50 · Reverse Holofoil US$2.10." */
export function printingCheckText(check: PrintingCheck, money: (usdCents: number) => string): string {
  const own = `In as ${check.chosen.label}${check.chosen.usdCents !== null ? ` (${money(check.chosen.usdCents)})` : ''}.`
  const others = check.others.map((o) => `${o.label} ${money(o.usdCents)}`).join(' · ')
  return others ? `${own} ${others}. Check which printing the card is.` : own
}
